import { useGetIdentity, useList, useUpdate } from "@refinedev/core";
import { Card, Space, Typography, Button, Row, Col, List, Skeleton } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { IconDisplay } from "../../components/IconDisplay";
import { ImageUpload } from "../../components/ImageUpload";
import { LottoCommissionCard } from "./LottoCommissionCard";
import { useState } from "react";

const { Title, Text } = Typography;

export const SettingsPage = () => {
  const navigate = useNavigate();
  const [showUploadMap, setShowUploadMap] = useState<Record<string, boolean>>({});
  const { mutate: updateTournamentType, isLoading: isUpdatingTournamentType, variables: tournamentVariables } = useUpdate();
  const { mutate: removeTournamentIcon, isLoading: isRemovingTournamentIcon, variables: tournamentRemoveVariables } = useUpdate();
  const { mutate: updateMissionType, isLoading: isUpdatingMissionType, variables: missionVariables } = useUpdate();
  const { mutate: removeMissionIcon, isLoading: isRemovingMissionIcon, variables: missionRemoveVariables } = useUpdate();

  const { data: identity, isLoading: identityLoading } = useGetIdentity<{
    id: string;
    name: string;
    email: string;
    role?: string;
    profilePictureURL?: string;
  }>();

  const isSuperAdmin = identity?.role === "SUPER_ADMIN";

  const { data: tournamentTypes, isLoading: tournamentTypesLoading, refetch: refetchTournamentTypes } = useList({
    resource: "tournament_type/for_select_list",
  });

  const { data: missionTypes, isLoading: missionTypesLoading, refetch: refetchMissionTypes } = useList({
    resource: "mission_type/for_select_list",
  });

  const handleTournamentIconUpdate = async (tournamentTypeId: string, fileId: string | null) => {
    try {
      updateTournamentType({
        resource: "tournament_type",
        id: tournamentTypeId,
        values: {
          iconID: fileId
        },
      });
      setShowUploadMap(prev => ({ ...prev, [tournamentTypeId]: false }));
      // Refetch tournament types to get updated data
      setTimeout(() => {
        refetchTournamentTypes();
      }, 500);
    } catch (error) {
      console.error('Failed to update tournament type:', error);
    }
  };

  const handleTournamentIconRemove = async (tournamentTypeId: string) => {
    try {
      removeTournamentIcon({
        resource: "tournament_type",
        id: tournamentTypeId,
        values: {
          iconID: null
        },
      });
      setShowUploadMap(prev => ({ ...prev, [tournamentTypeId]: false }));
      setTimeout(() => {
        refetchTournamentTypes();
      }, 500);
    } catch (error) {
      console.error('Failed to remove tournament icon:', error);
    }
  };

  const handleMissionIconUpdate = async (missionId: string, fileId: string | null) => {
    try {
      updateMissionType({
        resource: "mission_type",
        id: missionId,
        values: {
          iconID: fileId
        },
      });
      setShowUploadMap(prev => ({ ...prev, [`mission-${missionId}`]: false }));
      setTimeout(() => {
        refetchMissionTypes();
      }, 500);
    } catch (error) {
      console.error('Failed to update mission icon:', error);
    }
  };

  const handleMissionIconRemove = async (missionId: string) => {
    try {
      removeMissionIcon({
        resource: "mission_type",
        id: missionId,
        values: {
          iconID: null
        },
      });
      setShowUploadMap(prev => ({ ...prev, [`mission-${missionId}`]: false }));
      setTimeout(() => {
        refetchMissionTypes();
      }, 500);
    } catch (error) {
      console.error('Failed to remove mission icon:', error);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }} size="large">
      {/* User Profile Section */}
      <Card style={{ marginTop: 20, width: '70vw' }}>
        <Row align="middle" justify="space-between">
            {identityLoading || tournamentTypesLoading ? (    
                <Row style={{ width: '100%' }} align="middle" justify="space-between">
                    <Col>
                        <Space size="large" align="center">
                            <Skeleton.Avatar active size={100} shape="circle" />
                            <Space direction="vertical">
                                <Skeleton.Input active style={{ width: 200 }} />
                                <Skeleton.Input active style={{ width: 150 }} size="small" />
                            </Space>
                        </Space>
                    </Col>
                    <Col>
                        <Skeleton.Button active />
                    </Col>
                </Row>
            ) : (
                <>
                    <Col>
                        <Space size="large" align="center">
                            <Space
                              style={{
                                border: '1px solid #f0f0f0',
                                borderRadius: '50%',
                                padding: 4,
                                minWidth: 100,
                                minHeight: 100,
                              }}
                            >
                              <IconDisplay
                                iconURL={identity?.profilePictureURL}
                                width={100}
                                height={100}
                                borderRadius="50%"
                              />
                            </Space>
                          <Space direction="vertical">
                            <Title level={4}>{identity?.name}</Title>
                            <Text type="secondary">{identity?.email}</Text>
                          </Space>
                        </Space>
                    </Col>
                    <Col>
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/users/edit/${identity?.id}`)}
                      >
                        Edit Profile
                      </Button>
                    </Col>
                </>
            )}
        </Row>
      </Card>

      {/* Platform Lotto Commission — SUPER_ADMIN only (writes are gated server-side too) */}
      {isSuperAdmin && <LottoCommissionCard />}

      <Space style={{ width: '70vw', marginInline: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Tournament Types Section */}
        <Card title="Manage Tournament Icons">
          <List
            grid={{ gutter: 16, column: 2 }}
            loading={tournamentTypesLoading}
            dataSource={tournamentTypes?.data || []}
            renderItem={(item: any) => (
              <List.Item>
                <Card>
                  <Space direction="vertical" align="center" style={{ width: '100%' }}>
                    <Title level={5}>{item.value}</Title>
                    {showUploadMap[item.id]  ? (
                      <Space direction="vertical" align="center" style={{ width: '100%' }}>
                        <ImageUpload onChange={(fileID) => handleTournamentIconUpdate(item.id, fileID)} />
                        <Button onClick={() => setShowUploadMap(prev => ({ ...prev, [item.id]: false }))}>
                          Cancel
                        </Button>
                      </Space>                    ) : (
                      <Space direction="vertical" align="center" style={{ width: '100%' }}>
                        {item.iconID && (
                          <IconDisplay iconURL={`${import.meta.env.VITE_CDN_URL}/${item.iconID}`} />
                        )}
                        <Button
                          onClick={() => setShowUploadMap(prev => ({ ...prev, [item.id]: true }))}
                          loading={isUpdatingTournamentType && (tournamentVariables && tournamentVariables.id === item.id)}
                        >
                          {item.iconID ? "Change icon" : "Upload icon"}
                        </Button>
                        <Button
                          onClick={() => {
                            handleTournamentIconRemove(item.id);
                          }}
                          disabled={!item.iconID}
                          loading={isRemovingTournamentIcon && (tournamentRemoveVariables && tournamentRemoveVariables.id === item.id)}
                        >
                          Remove icon
                        </Button>
                        
                      </Space>
                    )}
                  </Space>
                </Card>
              </List.Item>
            )}
          />
        </Card>

        {/* Mission Types Section */}
        <Card title="Manage Mission Icons">
          <List
            grid={{ gutter: 16, column: 2 }}
            loading={missionTypesLoading}
            dataSource={missionTypes?.data || []}
            renderItem={(item: any) => (
              <List.Item>
                <Card>
                  <Space direction="vertical" align="center" style={{ width: '100%' }}>
                    <Title level={5}>{item.value}</Title>
                    {showUploadMap[`mission-${item.id}`] ? (
                      <Space direction="vertical" align="center" style={{ width: '100%' }}>
                        <ImageUpload onChange={(fileID) => handleMissionIconUpdate(item.id, fileID)} />
                        <Button onClick={() => setShowUploadMap(prev => ({ ...prev, [`mission-${item.id}`]: false }))}>
                          Cancel
                        </Button>
                      </Space>
                    ) : (
                      <Space direction="vertical" align="center" style={{ width: '100%' }}>
                        {item.iconID && (
                          <IconDisplay iconURL={`${import.meta.env.VITE_CDN_URL}/${item.iconID}`} />
                        )}
                        <Button
                          onClick={() => setShowUploadMap(prev => ({ ...prev, [`mission-${item.id}`]: true }))}
                          loading={isUpdatingMissionType && (missionVariables && missionVariables.id === item.id)}
                        >
                          {item.iconID ? "Change icon" : "Upload icon"}
                        </Button>
                        <Button
                          onClick={() => {
                            handleMissionIconRemove(item.id);
                          }}
                          disabled={!item.iconID}
                          loading={isRemovingMissionIcon && (missionRemoveVariables && missionRemoveVariables.id === item.id)}
                        >
                          Remove icon
                        </Button>
                      </Space>
                    )}
                  </Space>
                </Card>
              </List.Item>
            )}
          />
        </Card>
      </Space>
    </Space>
  );
}; 